import { useStore } from '@/state/store';
import { serializeFold, parseFold, foldToPattern } from '@/io/fold';

/**
 * A small, stable surface for the Playwright suite.
 *
 * Driving the app through pixels alone makes end-to-end tests brittle for the
 * wrong reasons (canvas hit-testing, GPU differences). The interactions that
 * matter - drawing, selecting, assigning - are still exercised through real
 * pointer events; this hook exists so assertions can read the state those
 * interactions produced, and so the 3D check can look at the actual simulation
 * frame instead of a screenshot.
 *
 * It is intentionally read-mostly and ships in production too, so the tests run
 * against exactly the bundle users get.
 */
export function installTestHooks(): void {
  const hooks = {
    getState: () => {
      const s = useStore.getState();
      return {
        pattern: s.pattern,
        analysis: {
          counts: s.analysis.counts,
          kawasakiPassing: s.analysis.kawasakiPassing,
          kawasakiApplicable: s.analysis.kawasakiApplicable,
          maekawaPassing: s.analysis.maekawaPassing,
          maekawaApplicable: s.analysis.maekawaApplicable,
          dof: s.analysis.dof,
          issues: s.analysis.issues.map((i) => ({ id: i.id, title: i.title })),
        },
        frame: s.frame ? { positions: Array.from(s.frame.positions) } : null,
        sim: { foldAmount: s.sim.foldAmount, mode: s.sim.mode },
      };
    },
    setFoldAmount: (v: number) => useStore.getState().setFoldAmount(v),
    setMode: (m: 'rigid' | 'compliant') => useStore.getState().setSim({ mode: m }),
    loadExample: (id: string) => useStore.getState().loadExample(id),
    exportFold: () => serializeFold(useStore.getState().pattern),
    importFold: (text: string) => {
      const { file } = parseFold(text);
      if (!file) throw new Error('unparseable FOLD');
      const { pattern } = foldToPattern(file);
      if (!pattern) throw new Error('no pattern in FOLD');
      useStore.getState().loadPattern(pattern, 'Test import');
    },
    /** Mass-free centroid of the current simulation frame. */
    centroid: (): [number, number, number] => {
      const frame = useStore.getState().frame;
      if (!frame) return [0, 0, 0];
      let x = 0;
      let y = 0;
      let z = 0;
      const n = frame.positions.length / 3;
      for (let i = 0; i < n; i++) {
        x += frame.positions[i * 3];
        y += frame.positions[i * 3 + 1];
        z += frame.positions[i * 3 + 2];
      }
      return [x / n, y / n, z / n];
    },
    /**
     * Largest |fold angle| over the creases, in degrees. This is the honest
     * measure of "is it folded": the sheet is free to tumble as a rigid body, so
     * a z-extent of zero is neither necessary nor sufficient.
     */
    maxFoldAngleDeg: () => {
      const frame = useStore.getState().frame;
      if (!frame) return 0;
      let max = 0;
      for (const a of frame.edgeFoldAngles) {
        if (Number.isFinite(a)) max = Math.max(max, Math.abs((a * 180) / Math.PI));
      }
      return max;
    },
    /** Largest |z| in the current simulation frame: is the sheet out of plane? */
    maxAbsZ: () => {
      const frame = useStore.getState().frame;
      if (!frame) return 0;
      let max = 0;
      for (let i = 2; i < frame.positions.length; i += 3) {
        max = Math.max(max, Math.abs(frame.positions[i]));
      }
      return max;
    },
  };
  (window as unknown as Record<string, unknown>).__foldTest = hooks;
}
